import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import {
  ArrowLeft,
  CheckCircle2,
  Clock,
  Coins,
  FileText,
  MapPin,
  Sparkles,
} from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { COMPANY } from "@/lib/company";
import type {
  DistrictServiceContent,
  DistrictServicePair,
} from "@/content/bangkok/builder";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import {
  buildBreadcrumbSchema,
  buildFaqSchema, buildSpeakableSchema, buildHowToStepsSchema } from "@/lib/schema";

type NearbyDistrict = { slug: string; nameTh: string; nameEn: string; zoneTh: string };
type DistrictServiceData = {
  pair: DistrictServicePair;
  content: DistrictServiceContent;
  nearby: NearbyDistrict[];
};

export const Route = createFileRoute("/bangkok/$district/$service")({
  loader: async ({ params }): Promise<DistrictServiceData> => {
    const [{ buildDistrictServiceContent, resolveDistrictServicePair }, districts, { pickNearby }] =
      await Promise.all([
        import("@/content/bangkok/builder"),
        import("@/content/bangkok/districts"),
        import("@/lib/nearby-links"),
      ]);
    const pair = resolveDistrictServicePair(params.district, params.service);
    if (!pair) throw notFound();
    const nearby = pickNearby(
      districts.BANGKOK_DISTRICTS,
      pair.district,
      (d) => d.zone,
      5,
    ).map((n) => ({
      slug: n.slug,
      nameTh: n.nameTh,
      nameEn: n.nameEn,
      zoneTh: districts.BANGKOK_ZONES[n.zone].th,
    }));
    return { pair, content: buildDistrictServiceContent(pair), nearby };
  },
  head: ({ params, loaderData }) => {
    const pair = loaderData?.pair;
    const site = getActiveSiteUrl();
    if (!pair || !loaderData) {
      return pageHeadForProvince({
        title: "ไม่พบหน้า",
        description: "ไม่พบหน้า",
        path: `/bangkok/${params.district}/${params.service}`,
      });
    }
    const c = loaderData.content;
    const base = pageHeadForProvince({
      title: c.metaTitle,
      description: c.metaDesc,
      path: `/bangkok/${pair.district.slug}/${pair.service.slug}`,
      type: "article",
    });
    return {
      ...base,
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: site },
              { name: "กรุงเทพ", url: `${site}/bangkok` },
              {
                name: pair.district.nameTh,
                url: `${site}/bangkok/${pair.district.slug}`,
              },
              {
                name: c.h1,
                url: `${site}/bangkok/${pair.district.slug}/${pair.service.slug}`,
              },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(c.faqs)) },
        { type: "application/ld+json", children: JSON.stringify(buildHowToStepsSchema({ name: `ขั้นตอน: ${c.h1}`, description: c.metaDesc, path: `/bangkok/${params.district}/${params.service}`, steps: c.process, inLanguage: "th" })) },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "@id": `${site}/bangkok/${pair.district.slug}/${pair.service.slug}#service`,
            name: c.h1,
            description: c.metaDesc,
            provider: { "@id": `${site}/#organization` },
            areaServed: {
              "@type": "AdministrativeArea",
              name: `${pair.district.nameEn} District, Bangkok`,
            },
            serviceType: pair.service.h1,
            url: `${site}/bangkok/${pair.district.slug}/${pair.service.slug}`,
          }),
        },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema(`/bangkok/${params.district}/${params.service}`)) },
      ],
    };
  },
  component: DistrictServicePage,
  notFoundComponent: () => (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-2xl px-4 py-24 text-center">
        <h1 className="text-2xl font-bold">ไม่พบหน้านี้</h1>
        <Link to="/bangkok" className="mt-4 inline-flex items-center gap-1 text-gold">
          <ArrowLeft className="h-4 w-4" /> กลับ
        </Link>
      </div>
      <SiteFooter />
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="p-10 text-center">
      <h1 className="text-xl font-bold">เกิดข้อผิดพลาด</h1>
      <p className="mt-2 text-sm text-muted-foreground">{error.message}</p>
    </div>
  ),
});

function DistrictServicePage() {
  const { pair, content: c, nearby } = Route.useLoaderData() as DistrictServiceData;
  const { district, service } = pair;
  const serviceLabel = service.metaTitle.replace(/\s*\|\s*Thai Notary.*/i, "").trim();

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main id="main-content">
        <nav className="border-b border-border bg-muted/30">
          <div className="mx-auto max-w-5xl px-4 py-3 text-xs text-muted-foreground">
            <Link to="/" className="hover:text-primary">หน้าแรก</Link>
            <span className="mx-2">/</span>
            <Link to="/bangkok" className="hover:text-primary">กรุงเทพ</Link>
            <span className="mx-2">/</span>
            <Link
              to="/bangkok/$district"
              params={{ district: district.slug }}
              className="hover:text-primary"
            >
              {district.nameTh}
            </Link>
            <span className="mx-2">/</span>
            <span className="text-foreground">{service.h1.slice(0, 40)}</span>
          </div>
        </nav>

        <section className="border-b border-border bg-gradient-to-b from-primary/5 to-background">
          <div className="mx-auto max-w-5xl px-4 py-12">
            <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-xs font-semibold text-gold">
              <Sparkles className="h-3.5 w-3.5" />
              เขต {district.nameTh} • {district.nameEn}
            </div>
            <h1 className="mt-3 text-3xl font-extrabold md:text-4xl">{c.h1}</h1>
            <p
              id="ai-answer-tldr"
              className="mt-4 rounded-2xl border border-gold/30 bg-gold/5 p-5 text-base leading-relaxed"
            >
              <span className="mb-1 block text-xs font-bold uppercase tracking-wider text-gold">
                สรุปสั้น
              </span>
              {c.tldr}
            </p>
          </div>
        </section>

        <section className="border-b border-border">
          <div className="mx-auto grid max-w-5xl gap-3 px-4 py-8 md:grid-cols-3">
            {c.quickFacts.map((f) => (
              <div key={f.label} className="rounded-xl border border-border bg-card p-4">
                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {f.label}
                </div>
                <div className="mt-1 text-sm font-bold">{f.value}</div>
              </div>
            ))}
          </div>
        </section>

        <section className="border-b border-border">
          <div className="mx-auto max-w-4xl px-4 py-12">
            <h2 className="flex items-center gap-2 text-2xl font-bold">
              <MapPin className="h-5 w-5 text-gold" />
              ทำไมเลือกเรา ในเขต {district.nameTh}
            </h2>
            <div className="mt-4 space-y-3 text-base leading-relaxed text-muted-foreground">
              {c.whyHere.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </div>
        </section>

        <section className="border-b border-border bg-muted/20">
          <div className="mx-auto max-w-4xl px-4 py-12">
            <h2 className="flex items-center gap-2 text-2xl font-bold">
              <Clock className="h-5 w-5 text-gold" /> ขั้นตอนการทำงาน
            </h2>
            <ol className="mt-6 space-y-4">
              {c.process.map((s) => (
                <li key={s.step} className="flex gap-4 rounded-xl border border-border bg-card p-5">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">
                    {s.step}
                  </div>
                  <div>
                    <h3 className="font-bold">{s.title}</h3>
                    <p className="mt-1 text-sm text-muted-foreground">{s.body}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="border-b border-border">
          <div className="mx-auto max-w-4xl px-4 py-12">
            <h2 className="flex items-center gap-2 text-2xl font-bold">
              <FileText className="h-5 w-5 text-gold" /> เอกสารที่ต้องเตรียม
            </h2>
            <ul className="mt-4 grid gap-2 md:grid-cols-2">
              {c.documents.map((d, i) => (
                <li key={i} className="flex items-start gap-2 rounded-lg border border-border bg-card p-3 text-sm">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                  <span>{d}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="border-b border-border bg-muted/20">
          <div className="mx-auto max-w-4xl px-4 py-12">
            <h2 className="flex items-center gap-2 text-2xl font-bold">
              <Coins className="h-5 w-5 text-gold" /> ราคาอ้างอิง
            </h2>
            <div className="mt-4 overflow-hidden rounded-xl border border-border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-4 py-2 text-left">รายการ</th>
                    <th className="px-4 py-2 text-left">ราคา</th>
                  </tr>
                </thead>
                <tbody>
                  {service.pricing.slice(0, 6).map((p: { item: string; price: string }, i: number) => (
                    <tr key={i} className="border-t border-border">
                      <td className="px-4 py-2">{p.item}</td>
                      <td className="px-4 py-2 font-bold">{p.price}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-3 text-xs text-muted-foreground">
              ราคาในเขต{district.nameTh} อาจมีค่าเดินทาง on-site เพิ่ม 200–500 บาท ตามระยะจริง
            </p>
          </div>
        </section>

        <section className="border-b border-border">
          <div className="mx-auto max-w-4xl px-4 py-12">
            <h2 className="text-2xl font-bold">คำถามที่พบบ่อย</h2>
            <div className="mt-4 space-y-3">
              {c.faqs.map((f, i) => (
                <details key={i} className="group rounded-xl border border-border bg-card p-4">
                  <summary className="cursor-pointer list-none font-bold">
                    <span className="mr-2 text-gold">Q.</span>
                    {f.q}
                  </summary>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {c.related.length > 0 && (
          <section className="border-b border-border bg-muted/20">
            <div className="mx-auto max-w-4xl px-4 py-12">
              <h2 className="text-xl font-bold">
                บริการอื่นในเขต {district.nameTh}
              </h2>
              <div className="mt-4 grid gap-3 md:grid-cols-2">
                {c.related.map((r) => (
                  <Link
                    key={r.slug}
                    to="/bangkok/$district/$service"
                    params={{ district: district.slug, service: r.slug }}
                    className="rounded-xl border border-border bg-card p-4 text-sm font-semibold transition hover:border-gold"
                  >
                    {r.label} →
                  </Link>
                ))}
              </div>
              <div className="mt-6">
                <Link
                  to="/bangkok/$district"
                  params={{ district: district.slug }}
                  className="text-sm font-bold text-primary hover:underline"
                >
                  ← กลับหน้าหลัก {district.nameTh}
                </Link>
              </div>
            </div>
          </section>
        )}

        <section className="border-b border-border">
          <div className="mx-auto max-w-4xl px-4 py-12">
            <h2 className="text-xl font-bold">
              {serviceLabel} ในเขตใกล้เคียง {district.nameTh}
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              ทีมงานเดินเอกสารข้ามเขตในกรุงเทพฯ ทุกวันทำการ หากคุณสะดวกนัดที่เขตอื่น
              เลือกพื้นที่ใกล้เคียงได้จากรายการนี้ หรือดู
              <Link to="/bangkok" className="mx-1 font-semibold text-primary hover:underline">
                บริการครบ 50 เขตกรุงเทพฯ
              </Link>
              และ
              <Link to="/provinces" className="mx-1 font-semibold text-primary hover:underline">
                จังหวัดทั่วประเทศ 77 จังหวัด
              </Link>
            </p>
            <div className="mt-4 grid gap-3 md:grid-cols-2">
              {nearby.map((n) => (
                <Link
                  key={n.slug}
                  to="/bangkok/$district/$service"
                  params={{ district: n.slug, service: service.slug }}
                  className="rounded-xl border border-border bg-card p-4 text-sm transition hover:border-gold"
                >
                  <span className="font-semibold">
                    {serviceLabel} เขต{n.nameTh}
                  </span>
                  <span className="mt-0.5 block text-xs text-muted-foreground">
                    โซน{n.zoneTh} · {n.nameEn}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-primary text-primary-foreground">
          <div className="mx-auto max-w-4xl px-4 py-12 text-center">
            <h2 className="text-2xl font-extrabold">{c.finalCta}</h2>
            <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
              <a href={`tel:${COMPANY.phoneIntl}`} className="rounded-md bg-gold px-5 py-2.5 text-sm font-bold text-foreground">
                โทร {COMPANY.phone}
              </a>
              <a
                href={`https://line.me/R/ti/p/${COMPANY.lineId}`}
                target="_blank"
                rel="noopener"
                className="rounded-md border border-primary-foreground/30 bg-primary-foreground/10 px-5 py-2.5 text-sm font-bold"
              >
                ทักไลน์ {COMPANY.lineId}
              </a>
            </div>
          </div>
        </section>
        <div className="mx-auto max-w-4xl px-4 pb-12">
          <FaqSourceNote topic="notary" />
        </div>

      </main>
      <SiteFooter />
    </div>
  );
}
